import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import type { IMaintenanceWindow } from '@zero-dash/types';
import {
  MAINTENANCE_DISMISSAL_STORAGE_PREFIX,
  MAX_TIMEOUT_DELAY_MS,
  MS_PER_MINUTE,
} from '@/constants/Maintenance/maintenance';
import type { IMaintenanceContext } from '@/types/Maintenance/Maintenance.types';
import { deriveMaintenancePhase, isMaintenanceRunning } from '@/utils/functions/maintenance';

const MaintenanceContext = createContext<IMaintenanceContext | undefined>(undefined);

type DismissalKind = 'popup' | 'reminder';

function dismissalStorageKey(eventId: string, kind: DismissalKind): string {
  return `${MAINTENANCE_DISMISSAL_STORAGE_PREFIX}.${eventId}.${kind}.dismissed`;
}

function readDismissed(eventId: string, kind: DismissalKind): boolean {
  try {
    return window.localStorage.getItem(dismissalStorageKey(eventId, kind)) === 'true';
  } catch {
    return false;
  }
}

function writeDismissed(eventId: string, kind: DismissalKind): void {
  try {
    window.localStorage.setItem(dismissalStorageKey(eventId, kind), 'true');
  } catch {
    // Storage unavailable — the pop-up simply reappears next visit.
  }
}

/**
 * Removes every persisted maintenance dismissal flag. Call this once the status
 * fetch confirms there is no active maintenance, so stale keys don't accumulate.
 */
export function clearMaintenanceDismissals(): void {
  try {
    const staleKeys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(MAINTENANCE_DISMISSAL_STORAGE_PREFIX)) staleKeys.push(key);
    }
    staleKeys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Storage unavailable — nothing to clean up.
  }
}

interface IProps {
  maintenanceWindow?: IMaintenanceWindow | null;
  isAdmin?: boolean;
  children: ReactNode;
}

export function MaintenanceProvider({
  maintenanceWindow = null,
  isAdmin = false,
  children,
}: IProps): JSX.Element {
  const eventId = maintenanceWindow?.id ?? '';

  const [now, setNow] = useState<Date>(() => new Date());
  const [isPopupDismissed, setIsPopupDismissed] = useState(
    () => (eventId ? readDismissed(eventId, 'popup') : false),
  );
  const [isReminderDismissed, setIsReminderDismissed] = useState(
    () => (eventId ? readDismissed(eventId, 'reminder') : false),
  );

  // A new event resets the dismissals, otherwise the previous event's
  // acknowledgement would suppress this one's notifications.
  useEffect(() => {
    setIsPopupDismissed(eventId ? readDismissed(eventId, 'popup') : false);
    setIsReminderDismissed(eventId ? readDismissed(eventId, 'reminder') : false);
  }, [eventId]);

  useEffect(() => {
    if (!maintenanceWindow) return undefined;

    const notifyMs = new Date(maintenanceWindow.notifyFrom).getTime();
    const startMs = new Date(maintenanceWindow.startsAt).getTime();
    const reminderMs = startMs - maintenanceWindow.reminderLeadMinutes * MS_PER_MINUTE;

    const timers: number[] = [];
    const schedule = (at: number): void => {
      const delay = at - Date.now();
      if (delay > 0 && delay <= MAX_TIMEOUT_DELAY_MS) {
        timers.push(window.setTimeout(() => setNow(new Date()), delay));
      }
    };
    schedule(notifyMs);
    schedule(reminderMs);
    schedule(startMs);

    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [maintenanceWindow]);

  const phase = deriveMaintenancePhase(maintenanceWindow, now);
  const isLockedOut = isMaintenanceRunning(maintenanceWindow, now)
    && Boolean(maintenanceWindow?.blocksAccess)
    && !isAdmin;

  const dismissPopup = useCallback((): void => {
    setIsPopupDismissed(true);
    if (eventId) writeDismissed(eventId, 'popup');
  }, [eventId]);

  const dismissReminder = useCallback((): void => {
    setIsReminderDismissed(true);
    if (eventId) writeDismissed(eventId, 'reminder');
  }, [eventId]);

  // Pop-up takes priority over the reminder and shows only during the notice
  // period — so dismissing one never re-surfaces the other on refresh.
  const isPopupOpen = (phase === 'scheduled' || phase === 'imminent')
    && !isPopupDismissed
    && !isLockedOut;
  const isReminderOpen = phase === 'imminent'
    && !isReminderDismissed
    && !isPopupOpen
    && !isLockedOut;

  const value = useMemo<IMaintenanceContext>(() => ({
    phase,
    maintenanceWindow,
    isLockedOut,
    isPopupOpen,
    isReminderOpen,
    dismissPopup,
    dismissReminder,
  }), [
    phase,
    maintenanceWindow,
    isLockedOut,
    isPopupOpen,
    isReminderOpen,
    dismissPopup,
    dismissReminder,
  ]);

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
}

export function useMaintenance(): IMaintenanceContext {
  const context = useContext(MaintenanceContext);

  if (!context) {
    throw new Error('useMaintenance must be used within a MaintenanceProvider');
  }

  return context;
}
